import { type PropsWithChildren, memo, useMemo } from "react";

import { type WithBreakpoints, getSizeWithBreakpoints } from "@react/utilities";
import { STOREFRONT_TRANSITION_DURATION } from "@tablescape/constants";
import { type TTailwindString, tw } from "classnames";

import { BadgeView } from "./Badge.view";

export type BadgeType =
  | "primary"
  | "secondary"
  | "tertiary"
  | "quaternary"
  | "quinary"
  | "senary";

type BadgeSize = "sm" | "md" | "lg";

type Props = {
  className?: TTailwindString;
  size?: BadgeSize | WithBreakpoints<BadgeSize>;
  type?: BadgeType;
};

const styles = {
  base: tw(
    "font-calibre",
    "font-semibold",
    "antialiased",
    "text-xs",
    "uppercase",
    "tracking-widest",
    "leading-none",
    "cursor-default",
    "flex",
    "justify-center",
    "items-center",
    "whitespace-nowrap",
    "box-border",
    "inline-flex",
    "flex-row",
    "justify-center",
    "py-0.5",
    "rounded",
    "transition-all",
    STOREFRONT_TRANSITION_DURATION
  ),
  type: {
    primary: tw("bg-interactive-char", "text-bone-50"),
    secondary: tw("bg-bone-300", "text-interactive-char"),
    tertiary: tw("bg-sage-700", "text-bone-50"),
    quaternary: tw("bg-interactive-brick", "text-bone-50"),
    quinary: tw(
      "bg-transparent",
      "text-secondary",
      "border-solid",
      "border-secondary",
      "border"
    ),
    senary: tw("bg-misc-bright-blue", "text-white"),
  },
  sm: {
    sm: tw("h-4", "px-2"),
    md: tw("h-6", "px-2"),
    lg: tw("h-8", "px-2"),
  },
  md: {
    sm: tw("md:h-4", "md:px-2"),
    md: tw("md:h-6", "md:px-2"),
    lg: tw("md:h-8", "md:px-2"),
  },
  lg: {
    sm: tw("lg:h-4", "lg:px-2"),
    md: tw("lg:h-6", "lg:px-2"),
    lg: tw("lg:h-8", "lg:px-2"),
  },
};

const BadgeBase = ({
  children,
  className,
  size: originalSize = "md",
  type = "primary",
}: PropsWithChildren<Props>) => {
  const size = useMemo(() => {
    const resolvedSize =
      typeof originalSize === "string"
        ? { sm: originalSize, md: originalSize }
        : { ...originalSize };

    return getSizeWithBreakpoints(resolvedSize);
  }, [originalSize]);

  const badgeClass = useMemo(
    () =>
      tw(
        styles.base,
        {
          [styles.type.primary]: type === "primary",
          [styles.type.secondary]: type === "secondary",
          [styles.type.tertiary]: type === "tertiary",
          [styles.type.quaternary]: type === "quaternary",
          [styles.type.quinary]: type === "quinary",
          [styles.type.senary]: type === "senary",
        },

        {
          [styles.sm.sm]: size.sm === "sm",
          [styles.sm.md]: size.sm === "md",
          [styles.sm.lg]: size.sm === "lg",
        },
        {
          [styles.md.sm]: size.md === "sm",
          [styles.md.md]: size.md === "md",
          [styles.md.lg]: size.md === "lg",
        },
        {
          [styles.lg.sm]: size.lg === "sm",
          [styles.lg.md]: size.lg === "md",
          [styles.lg.lg]: size.lg === "lg",
        },
        className ? tw(className) : undefined
      ),
    [className, size.sm, size.md, size.lg, type]
  );

  return <BadgeView className={badgeClass}>{children}</BadgeView>;
};

export const Badge = memo(BadgeBase);
